// Third-party
import ReactDOMServer from 'react-dom/server';
import React from 'react';

// Types
import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';

// Components
import { CurrencyCell, NotApplicableCell, PercentageCell, TextCell, HTMLCell } from '../../../../../views/components/cells';
import HoldingIcon, { Props as HoldingIconProps } from '../../../../../views/components/common/HoldingIcon';

// Utils
import { getProposalDistributionScenarios } from '../../../utils/income-stream';
import IncomeStreamScenario from '@/types/income-stream-scenario';

export default class IncomeStreamDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new IncomeStreamDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected build(): void {
    const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();

    const daoConfig: ProposalNS.IncomeStreamConfig = this.proposalElement.DAO as ProposalNS.IncomeStreamConfig;
    const { distributionTable } = daoConfig;

    // there should only be one item
    if (itemDAOs.length === 1) {
      const { item } = itemDAOs[0];

      if (item.scenarios !== undefined) {
        const { defaultIncomeStream, incomeStreamDistribution } = item.scenarios;
        let distributionScenarios: IncomeStreamScenario[] = [];

        if (defaultIncomeStream !== undefined && defaultIncomeStream.data != null) {
          // top header
          const headerClasses = [ProposalStyleClassNames.ProposalTableContent, ProposalStyleClassNames.ProposalTableFieldName].join(' ');
          this.addFieldName('', headerClasses);

          const holdingIcon: string = ReactDOMServer.renderToString(
            React.createElement<HoldingIconProps>(HoldingIcon, {
              type: item.type,
              color: item.color,
            }),
          );

          const textWithIcon = (text: string) => `
              <div class="text-icon">
                ${holdingIcon}
                <span>${text}</span>
              </div>
            `;

          const baseScenario = textWithIcon(this.formatMessage('Proposal.Modules.IncomeStream.BaseScenario'));

          this.addItemField(itemDAOs[0], '', baseScenario, HTMLCell, 0, 2);
          this.addItemField(itemDAOs[0], '', '', TextCell, 1, 0);

          distributionScenarios = getProposalDistributionScenarios(this.proposal.options.orientation, incomeStreamDistribution);

          if (distributionScenarios !== undefined) {
            for (let i = 0; i < distributionScenarios.length; i++) {
              const scenario = textWithIcon(this.formatMessage('Proposal.Modules.IncomeStream.Scenario', { key: i + 1 }));
              this.addItemField(itemDAOs[0], '', scenario, HTMLCell, 2 * (i + 1), 2);
              this.addItemField(itemDAOs[0], '', '', TextCell, 2 * (i + 1) + 1, 0);
            }
          }

          // second header
          this.addFieldName(this.formatMessage('Proposal.Modules.IncomeStream.Year'), ProposalStyleClassNames.ProposalTableContent);
          const senarioCount = 1 + distributionScenarios.length;
          for (let i = 0; i < senarioCount; i++) {
            if (!distributionTable) {
              this.addItemField(itemDAOs[0], '', this.formatMessage('Proposal.Modules.IncomeStream.Reinvested'), TextCell, 2 * i, undefined, [
                ProposalStyleClassNames.ProposalTableFieldName,
              ]);
              this.addItemField(itemDAOs[0], '', this.formatMessage('Proposal.Modules.IncomeStream.Distributions'), TextCell, 2 * i + 1, undefined, [
                ProposalStyleClassNames.ProposalTableFieldName,
              ]);
            } else {
              this.addItemField(itemDAOs[0], '', this.formatMessage('Proposal.Modules.IncomeStream.Principal'), TextCell, 2 * i, undefined, [
                ProposalStyleClassNames.ProposalTableFieldName,
              ]);
              this.addItemField(itemDAOs[0], '', this.formatMessage('Proposal.Modules.IncomeStream.Distribution'), TextCell, 2 * i + 1, undefined, [
                ProposalStyleClassNames.ProposalTableFieldName,
              ]);
            }
          }

          // content
          const scenarios = [defaultIncomeStream.data.results];
          for (const distributionScenario of distributionScenarios) {
            if (distributionScenario.data != null) {
              scenarios.push(distributionScenario.data.results);
            }
          }

          const keys: string[] = Object.keys(defaultIncomeStream.data.results);
          keys.sort().reverse();
          for (const year of keys) {
            this.addFieldName(year, ProposalStyleClassNames.ProposalTableContent);
            for (let i = 0; i < scenarios.length; i++) {
              const scenario = scenarios[i];

              if (distributionTable) {
                const cellType = scenario[year].average === 0 ? NotApplicableCell : PercentageCell;
                this.addItemField(itemDAOs[0], '', scenario[year].gainPercent.toFixed(2), cellType, 2 * i);
                this.addItemField(itemDAOs[0], '', scenario[year].distributionPercent.toFixed(2), cellType, 2 * i + 1);
              } else {
                this.addItemField(itemDAOs[0], '', { value: scenario[year].capital }, CurrencyCell, 2 * i);
                this.addItemField(itemDAOs[0], '', { value: scenario[year].average }, CurrencyCell, 2 * i + 1);
              }
            }
          }
        }
      }
    }
  }

  // private methods

  private addItemField(
    itemDAO: ComparisonItemDAO,
    name: string,
    value: string | { value: number },
    cellType: any,
    col: number,
    colSpan?: number,
    classes?: string[],
  ): void {
    const classNames = [...(classes || []), ProposalStyleClassNames.ProposalTableContent];
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, classNames.join(' '));

    field.cellType = value != null ? cellType : NotApplicableCell;
    field.decimalPlace = 0;
    field.withSuffixes = false;

    if (colSpan !== undefined) {
      field.colSpan = colSpan;
    }

    this.addFieldDAO(itemDAO, field, col);
  }
}
